import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress,
} from "@mui/material";
import { FormProvider, Controller } from "react-hook-form";
import { URLS } from "../../../constants";
import { useCompanyForm } from "../../../hooks/useCompanyForm";
import { CustomStepConnector, CustomStepIcon } from "./CustomStepper";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../../utils/api";
import { MuiTelInput } from "mui-tel-input";
import { PHONE_CONFIG } from "../../../constants";
import { createPhoneNumberHandler } from "../../../utils/phoneNumberInputHandler";
import logger from "../../../utils/logger";

interface CompanyRegisterProps {
  onSuccess?: () => void;
  onError?: () => void;
}

const CompanyRegister: React.FC<CompanyRegisterProps> = ({
  onSuccess,
  onError,
}) => {
  const { methods, steps, activeStep, handleNext, handleBack } =
    useCompanyForm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Ao enviar, convertemos "telephone" para um array (ex.: para futuras expansões)
  const handleSubmit = async (data: any) => {
    setLoading(true);
    const modifiedData = {
      ...data,
      telephone: data.telephone ? [data.telephone] : [],
    };
    try {
      const response = await api.post(
        `${URLS.CLIENT_URL}/api/signup-company`,
        modifiedData,
      );
      // Store the user data in localStorage and cookies
      const userData = response.data;

      // Check if we have the necessary data

      // Store in localStorage for auth checks
      localStorage.setItem("authUser", JSON.stringify(userData));

      // Navigate based on whether company has companyId
      // Check both companyId and companyID for compatibility
      const companyId = userData.companyId || userData.companyID;
      if (!companyId || companyId === "" || companyId === "0") {
        // Redirect to onboarding if no companyId
        navigate("/onboarding");
      } else {
        // Redirect to channel-session if companyId exists
        navigate("/channel-session");
      }

      if (onSuccess) onSuccess();
    } catch (error) {
      logger.error("Erro no registro da empresa:", error);
      // Integração futura: capturar erro com Sentry ou similar
      if (onError) onError();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h5" align="center" gutterBottom>
        {t("companyRegister.title")}
      </Typography>
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        connector={<CustomStepConnector />}
      >
        {steps.map((label, index) => (
          <Step key={index}>
            <StepLabel StepIconComponent={CustomStepIcon}>
              {`${index + 1}. ${t(`companyRegister.steps.${index}`)}`}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          {/* Etapa 1: Dados Básicos */}
          {activeStep === 0 && (
            <>
              <Controller
                name="name"
                control={methods.control}
                defaultValue=""
                rules={{ required: t("companyRegister.fields.nameRequired") }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t("companyRegister.fields.name")}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error ? fieldState.error.message : ""
                    }
                    inputProps={{
                      "aria-label": t("companyRegister.fields.name"),
                    }}
                  />
                )}
              />
              <Controller
                name="fantasy_name"
                control={methods.control}
                defaultValue=""
                rules={{
                  required: t("companyRegister.fields.fantasyNameRequired"),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t("companyRegister.fields.fantasy_name")}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error ? fieldState.error.message : ""
                    }
                    inputProps={{
                      "aria-label": t("companyRegister.fields.fantasy_name"),
                    }}
                  />
                )}
              />
              <Controller
                name="cnpj"
                control={methods.control}
                defaultValue=""
                rules={{
                  required: t("companyRegister.fields.cnpjRequired"),
                  pattern: {
                    value: /^\d{14}$/,
                    message: t("companyRegister.fields.cnpjInvalid"),
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t("companyRegister.fields.cnpj")}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error ? fieldState.error.message : ""
                    }
                    inputProps={{
                      "aria-label": t("companyRegister.fields.cnpj"),
                    }}
                  />
                )}
              />
              <Controller
                name="area"
                control={methods.control}
                defaultValue=""
                rules={{ required: t("companyRegister.fields.areaRequired") }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t("companyRegister.fields.area")}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error ? fieldState.error.message : ""
                    }
                    inputProps={{
                      "aria-label": t("companyRegister.fields.area"),
                    }}
                  />
                )}
              />
            </>
          )}

          {/* Etapa 2: Contato */}
          {activeStep === 1 && (
            <>
              <Controller
                name="telephone"
                control={methods.control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <MuiTelInput
                    value={field.value || ""}
                    onChange={createPhoneNumberHandler(
                      field.onChange,
                      field.value,
                    )}
                    defaultCountry={PHONE_CONFIG.DEFAULT_COUNTRY}
                    label={t("companyRegister.fields.telephone")}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error ? (fieldState.error.message as any) : ""
                    }
                    forceCallingCode
                    size="small"
                    sx={{
                      "& .MuiInputBase-root": {
                        backgroundColor: "transparent",
                      },
                    }}
                  />
                )}
              />
              <Controller
                name="email"
                control={methods.control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t("companyRegister.fields.email")}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error ? fieldState.error.message : ""
                    }
                    inputProps={{
                      "aria-label": t("companyRegister.fields.email"),
                    }}
                  />
                )}
              />
              <Controller
                name="website"
                control={methods.control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t("companyRegister.fields.website")}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error ? fieldState.error.message : ""
                    }
                    inputProps={{
                      "aria-label": t("companyRegister.fields.website"),
                    }}
                  />
                )}
              />
              <Controller
                name="instagram_profile"
                control={methods.control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t("companyRegister.fields.instagram_profile")}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error ? fieldState.error.message : ""
                    }
                    inputProps={{
                      "aria-label": t(
                        "companyRegister.fields.instagram_profile",
                      ),
                    }}
                  />
                )}
              />
            </>
          )}

          {/* Etapa 3: Endereço e Assinatura */}
          {activeStep === 2 && (
            <>
              <FormControl fullWidth margin="normal">
                <InputLabel>
                  {t("companyRegister.fields.subscription")}
                </InputLabel>
                <Controller
                  name="subscription"
                  control={methods.control}
                  defaultValue="BASIC"
                  render={({ field }) => (
                    <Select
                      {...field}
                      label={t("companyRegister.fields.subscription")}
                      inputProps={{
                        "aria-label": t("companyRegister.fields.subscription"),
                      }}
                    >
                      <MenuItem value="BASIC">BASIC</MenuItem>
                      <MenuItem value="GOLD">GOLD</MenuItem>
                      <MenuItem value="PREMIUM">PREMIUM</MenuItem>
                      <MenuItem value="ON DEMAND">ON DEMAND</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel>
                  {t("companyRegister.fields.subscription_message")}
                </InputLabel>
                <Controller
                  name="subscription_message"
                  control={methods.control}
                  defaultValue="2K"
                  render={({ field }) => (
                    <Select
                      {...field}
                      label={t("companyRegister.fields.subscription_message")}
                      inputProps={{
                        "aria-label": t(
                          "companyRegister.fields.subscription_message",
                        ),
                      }}
                    >
                      <MenuItem value="2K">2K</MenuItem>
                      <MenuItem value="6K">6K</MenuItem>
                      <MenuItem value="10K">10K</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>

              <Controller
                name="address.zip_code"
                control={methods.control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t("companyRegister.fields.zip_code")}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error ? fieldState.error.message : ""
                    }
                    inputProps={{
                      "aria-label": t("companyRegister.fields.zip_code"),
                    }}
                  />
                )}
              />
              <Controller
                name="address.street"
                control={methods.control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t("companyRegister.fields.street")}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error ? fieldState.error.message : ""
                    }
                    inputProps={{
                      "aria-label": t("companyRegister.fields.street"),
                    }}
                  />
                )}
              />
              <Controller
                name="address.number"
                control={methods.control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t("companyRegister.fields.number")}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error ? fieldState.error.message : ""
                    }
                    inputProps={{
                      "aria-label": t("companyRegister.fields.number"),
                    }}
                  />
                )}
              />
              <Controller
                name="address.city"
                control={methods.control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t("companyRegister.fields.city")}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error ? fieldState.error.message : ""
                    }
                    inputProps={{
                      "aria-label": t("companyRegister.fields.city"),
                    }}
                  />
                )}
              />
              <Controller
                name="address.state"
                control={methods.control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t("companyRegister.fields.state")}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error ? fieldState.error.message : ""
                    }
                    inputProps={{
                      "aria-label": t("companyRegister.fields.state"),
                    }}
                  />
                )}
              />
              <Controller
                name="address.country"
                control={methods.control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t("companyRegister.fields.country")}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error ? fieldState.error.message : ""
                    }
                    inputProps={{
                      "aria-label": t("companyRegister.fields.country"),
                    }}
                  />
                )}
              />
            </>
          )}
          <Box display="flex" justifyContent="space-between" mt={4}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              {t("companyRegister.backButton")}
            </Button>
            <Button
              variant="contained"
              type={activeStep === steps.length - 1 ? "submit" : "button"}
              onClick={activeStep === steps.length - 1 ? undefined : handleNext}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : activeStep === steps.length - 1 ? (
                t("companyRegister.submitButton")
              ) : (
                t("companyRegister.nextButton")
              )}
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Box>
  );
};

export default CompanyRegister;
